import {
  ProjectCreateModel,
  ProjectEditModel,
  ProjectModel,
} from 'shared/domain/project/types/model';
import { DayMomentValidation } from 'shared/types/time';
import { dateTimeToDayMomentDateTime } from 'shared/utils/date/dates';
import { anyToSupportedISO6391LanguageCode } from 'shared/utils/locale/anyToSupportedISO6391LanguageCode';
import { parseLocalizedNumberString } from 'shared/utils/parseLocalizedNumberString';
import { isNullish } from 'helpers/misc';
import { ProjectInDto } from '../types/dto';
import { ProjectEntity } from '../types/entity';
import { ProjectCreateOnView, ProjectEditOnView } from '../types/view';

function parseBudgetFromOnView(
  budget: string | number
): number | undefined {
  if (typeof budget === 'number') {
    return Number.isFinite(budget) ? budget : undefined;
  }

  return parseLocalizedNumberString(budget);
}

export function projectEntityToModel(
  projectEntity: ProjectEntity
): ProjectModel {
  const {
    createdAt,
    deleted,
    modifiedAt,
    _id,
    clientId,
    name,
    timezone,
    budget,
    currency,
    projectStartDate,
    projectEndDate,
    localeCode,
    processes,
  } = projectEntity;

  return {
    createdAt,
    deleted: Boolean(deleted),
    modifiedAt,
    _id,
    organizationId: clientId,
    name,
    timezone,
    budget,
    currency,
    projectStartDate,
    projectEndDate,
    localeCode,
    processes,
  };
}

export function projectInDtoToModel(projectInDto: ProjectInDto): ProjectModel {
  const {
    createdAt,
    deleted,
    modifiedAt,
    _id,
    clientId,
    name,
    timezone,
    budget,
    currency,
    projectStartDate,
    projectEndDate,
    localeCode,
    processes,
  } = projectInDto;

  return {
    createdAt,
    deleted: Boolean(deleted),
    modifiedAt,
    _id,
    organizationId: clientId,
    name,
    timezone,
    budget,
    currency,
    projectStartDate,
    projectEndDate,
    localeCode: anyToSupportedISO6391LanguageCode(localeCode),
    processes,
  };
}

export function projectCreateOnViewToProjectCreateModel({
  name,
  organization,
  currency,
  locale,
  processes,
  timezone,

  projectStartDate,
  projectEndDate,
  budget,
}: ProjectCreateOnView): ProjectCreateModel {
  const projectCreateModel: ProjectCreateModel = {
    name,
    organizationId: organization._id,
    currency: currency._id,
    localeCode: locale._id,
    processes: processes.map((process) => process._id),
    timezone: timezone._id,
  };

  if (budget !== undefined && budget !== null && budget !== '') {
    const parsedBudget = parseBudgetFromOnView(budget);
    if (parsedBudget !== undefined) {
      projectCreateModel.budget = parsedBudget;
    }
  }
  if (projectStartDate) {
    projectCreateModel.projectStartDate =
      dateTimeToDayMomentDateTime(
        projectStartDate,
        DayMomentValidation.start
      )?.toISO() || undefined;
  }
  if (projectEndDate) {
    projectCreateModel.projectEndDate =
      dateTimeToDayMomentDateTime(
        projectEndDate,
        DayMomentValidation.end
      )?.toISO() || undefined;
  }

  return projectCreateModel;
}

export function projectEditOnViewToEditModel(
  project: ProjectEditOnView
): ProjectEditModel {
  const budgetIsCleared =
    isNullish(project.budget) || project.budget === '';

  const mapped = projectCreateOnViewToProjectCreateModel(project);

  return {
    ...mapped,
    ...(budgetIsCleared ? { budget: null } : {}),
    _id: project._id,
  };
}
